import React from 'react';
import { ComplianceCheck } from '../../utils/types';
import { T, SEVERITY_COLOR } from '../../designTokens';

interface CipaExposurePanelProps {
  /** Checks from `result.sections.cipa`. Never mixed into the CCPA check list. */
  checks: ComplianceCheck[];
}

/**
 * CIPA § 631 exposure signals (issue #1392), rendered apart from FixList and
 * PassingChecks on purpose: those two read the four CCPA sections, and a reader
 * must not take a prior-consent exposure signal under a different statute for a
 * CCPA failure, or for a legal conclusion. Renders nothing when the section
 * raised no warning, so a clean scan shows no CIPA block at all.
 */
export default function CipaExposurePanel({ checks }: CipaExposurePanelProps) {
  const warnings = checks.filter((c) => c.status === 'warning');
  if (warnings.length === 0) return null;

  return (
    <section
      aria-labelledby="cipa-exposure-heading"
      style={{
        background: T.white,
        border: `1px solid ${T.gray200}`,
        borderRadius: T.radiusLg,
        padding: 20,
        marginTop: 32,
      }}
    >
      <h3
        id="cipa-exposure-heading"
        style={{ fontFamily: T.fontBody, fontSize: 18, fontWeight: 600, color: T.gray900, margin: 0 }}
      >
        CIPA exposure (Cal. Penal Code § 631)
      </h3>
      <p style={{ fontFamily: T.fontBody, fontSize: 14, color: T.gray600, margin: '8px 0 0' }}>
        These findings sit under a different California law from the rest of this report and do not affect
        your CCPA score. They show which tools that can receive what visitors type loaded before any consent
        choice. They are exposure signals, not findings of a violation: whether a vendor counts as a third party
        is a question for your counsel.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        {warnings.map((check) => (
          <div
            key={check.id}
            style={{
              border: `1px solid ${T.gray200}`,
              borderLeft: `2px solid ${SEVERITY_COLOR[check.severity] ?? T.gray400}`,
              borderRadius: T.radiusMd,
              padding: 16,
            }}
          >
            <div style={{ fontFamily: T.fontBody, fontSize: 15, fontWeight: 600, color: T.gray800 }}>
              {check.name}
            </div>
            <div style={{ fontFamily: T.fontBody, fontSize: 14, color: T.gray700, marginTop: 4 }}>
              {check.finding || check.details}
            </div>
            {check.fix?.summary && (
              <div style={{ fontFamily: T.fontBody, fontSize: 14, color: T.gray600, marginTop: 8 }}>
                <strong style={{ color: T.gray700 }}>How to reduce exposure: </strong>
                {check.fix.summary}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
